import Echo from 'laravel-echo';
import { useEffect, useMemo, useState } from 'react';
import 'share-menu';
import Api from '../api';
import { FuneralServiceSummary, FuneralServiceVideo, Organization } from '../types';
import { ServiceContext } from './Contexts/ServiceContext';
import { SettingsContext } from './Contexts/SettingsContext';
import { connectToEcho, switchEchoChannel } from './Helpers/echo';
import { getService } from './Helpers/getService';
import { ServiceStyle1 } from './Partials/ServiceStyle1/ServiceStyle1';
import { ServiceStyle2 } from './Partials/ServiceStyle2/ServiceStyle2';
import { PasswordModal } from './Partials/Shared/PasswordModal';

const Service = ({
  serviceHash,
  organizationHash,
  password,
}: {
  serviceHash: string | null;
  organizationHash: string | null;
  password: string | undefined;
}) => {
  const [service, setService] = useState<FuneralServiceSummary | null>();
  const [validPassword, setValidPassword] = useState(true);
  const [organization, setOrganization] = useState<Organization | null>(null);

  useEffect(() => {
    if (!organization && organizationHash) {
      Api.organization(organizationHash).then((data) => {
        setOrganization(data);
      });
    }
  }, []);

  const isUniversal = organization?.type === 'universal';

  useEffect(() => {
    if (!!service) {
      connectToEcho(setEcho, {
        wss_key: service?.wss_key,
        pusher_cluster: service?.pusher_cluster,
        wss_auth_url: service?.wss_auth_url,
        wss_host: service.wss_host,
        wss_port: service.wss_port,
      });
      setViewerStats(service?.viewer_stats ?? false);
    } else {
      getService(serviceHash, setService, setValidPassword, password);
    }
  }, [service]);

  const [liveSources, setLiveSources] = useState<[string, boolean][] | null>();
  const [selectedSource, setSelectedSource] = useState<{}[] | FuneralServiceVideo | undefined>();
  const [echo, setEcho] = useState<Echo | undefined>();
  const [isLive, setIsLive] = useState(false);
  const [viewerStats, setViewerStats] = useState(false);
  const [viewerCount, setViewerCount] = useState(0);
  const [wasWatching, setWasWatching] = useState(isLive);

  const settings = useMemo(() => {
    const settings = {
      ...service,
    };
    delete settings.service;
    return settings;
  }, []);

  const [eosms, setEosms] = useState<
    {
      display_after: string;
      message: string;
    }[]
  >([]);

  useEffect(() => {
    const echoData = {
      channel: viewerStats ? 'public' : 'private',
      echoConnection: echo,
      code: serviceHash!,
      viewerStats: viewerStats,
    };

    const serviceData = {
      service: service?.service,
      viewerCount: viewerCount,
      isLive: isLive,
      selectedSource: selectedSource,
    };

    const setters = {
      setLive: setIsLive,
      setLiveSources: setLiveSources,
      setViewerStats: setViewerStats,
      setViewerCount: setViewerCount,
      setSelectedSource: setSelectedSource,
    };

    switchEchoChannel(echoData, serviceData, setters);
  }, [echo]);

  useEffect(() => {
    if (service) {
      if (isLive && !selectedSource) {
        setSelectedSource(service?.service?.live_sources[0]);
      }
      setLiveSources(service?.service?.live_sources);
      setIsLive(service?.service?.has_live_stream);
      setEosms(service?.service?.end_of_stream_messages);
      setWasWatching(service?.service?.has_live_stream);
    }
  }, [service]);

  useEffect(() => {
    if (liveSources?.length && !selectedSource && isLive) {
      setSelectedSource(liveSources[0]);
    } else if (!liveSources?.length) {
      setSelectedSource(undefined);
    }
  }, [liveSources]);

  if (!service) return null;

  if (document.getElementById('fs-loader-container')) {
    //@ts-ignore
    document.getElementById('fs-loader-container').style.display = 'none';
  }

  if (!service.unlocked && service.fully_protected) {
    return (
      <PasswordModal onPasswordSubmit={(password) => getService(serviceHash, setService, setValidPassword, password)} validPassword={validPassword} />
    );
  }

  if (service.embed_service_style == '2' && !isUniversal) {
    return (
      <ServiceContext.Provider value={service.service}>
        <SettingsContext.Provider value={settings as FuneralServiceSummary}>
          <ServiceStyle2
            service={service}
            isLive={isLive}
            setService={setService}
            serviceHash={serviceHash}
            setSelectedSource={setSelectedSource}
            selectedSource={selectedSource}
            livePlayerSources={liveSources}
            eosms={eosms}
            wasWatching={wasWatching}
            viewerCount={viewerCount}
            viewerStats={viewerStats}
            setValidPassword={setValidPassword}
            validPassword={validPassword}
          />
        </SettingsContext.Provider>
      </ServiceContext.Provider>
    );
  }

  return (
    <ServiceContext.Provider value={service.service}>
      <SettingsContext.Provider value={settings as FuneralServiceSummary}>
        <ServiceStyle1
          service={service}
          isLive={isLive && !isUniversal}
          setService={setService}
          serviceHash={serviceHash}
          setSelectedSource={setSelectedSource}
          selectedSource={selectedSource}
          livePlayerSources={liveSources}
          eosms={eosms}
          wasWatching={wasWatching}
          viewerCount={viewerCount}
          viewerStats={viewerStats}
          setValidPassword={setValidPassword}
          validPassword={validPassword}
          universalOverride={isUniversal}
        />
      </SettingsContext.Provider>
    </ServiceContext.Provider>
  );
};

export default Service;
